import type React from 'react';
import { Suspense, useMemo } from 'react';
import { LibraryLayout } from '../common/LibraryLayout';
import { ErrorBoundary } from 'react-error-boundary';
import { useSuspenseQuery, usePrefetchQuery } from '@tanstack/react-query';
import { useFolderNavigation } from '../common/useFolderNavigation';
import { imagesQueries } from 'services/content/imagesQueries';
import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs';
import { ImagesLayout } from './ImagesLayout';
import Skeleton from '@mui/material/Skeleton';
import { ImagesListErrorState } from '../images-list/ImagesListErrorState';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { ROOT_ID } from '../common/folderNavigation';
import { SearchAssetsInput } from '../common/SearchAssetsInput';
import { LibraryEmptySearchResults } from '../common/LibraryEmptySearchResults';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import type { ContentWithFolder } from '../common/folderSearch';
import { useLibrarySearch } from '../common/useLibrarySearch';

type DraggableImageComponent = React.ComponentType<{ url: string; title: string }>;

function ImageLoader({ id, folderName, DraggableImage }: { id: string; folderName?: string; DraggableImage: DraggableImageComponent }) {
  const query = useMemo(() => imagesQueries.getById(id), [id]);
  const { data: image } = useSuspenseQuery(query);

  if (!image) return null;

  return (
    <Stack direction="column" alignItems="center" gap={0.25}>
      <DraggableImage url={image.content} title={image.name} />
      {folderName && (
        <Stack direction="row" alignItems="center" gap={0.25} sx={{ maxWidth: '6.5rem' }}>
          <FolderRoundedIcon sx={{ color: 'text.secondary', fontSize: '0.625rem', flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.625rem' }}>
            {folderName}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

function LibraryImagesContent({ DraggableImage }: { DraggableImage: React.ComponentType<{ url: string; title: string }> }) {
  const { data: rootFolders } = useSuspenseQuery(imagesQueries.listFolders);
  const { breadcrumbs, currentFolder, selectFolder } = useFolderNavigation(rootFolders, 'All');

  const { searchTerm, setSearchTerm, folders, contents, handleFolderSelect, isAtRoot, isSearchEmpty } = useLibrarySearch(
    rootFolders,
    currentFolder,
    selectFolder
  );

  return (
    <LibraryLayout.Container>
      <SearchAssetsInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} sx={{ width: '100%' }} />

      {breadcrumbs.length > 1 && !searchTerm && (
        <LibraryLayout.Navigation>
          <Breadcrumbs items={breadcrumbs} onSelectPath={handleFolderSelect} />
        </LibraryLayout.Navigation>
      )}

      {isSearchEmpty ? (
        <LibraryEmptySearchResults />
      ) : (
        <>
          {folders.length > 0 && (
            <LibraryLayout.Section title="Folders">
              {folders.map((folder) => (
                <LibraryLayout.FolderListItem key={folder.id ?? ROOT_ID} onClick={() => handleFolderSelect(folder.id)}>
                  {folder.name}
                </LibraryLayout.FolderListItem>
              ))}
            </LibraryLayout.Section>
          )}

          <LibraryLayout.Section title="Images">
            <ImagesLayout.Gallery>
              {(contents as ContentWithFolder[]).map((image) => (
                <ImageLoader
                  key={image.id}
                  id={image.id}
                  folderName={isAtRoot && searchTerm ? image.folderName : undefined}
                  DraggableImage={DraggableImage}
                />
              ))}
            </ImagesLayout.Gallery>

            {contents.length === 0 && !searchTerm && (
              <Typography variant="caption" color="text.secondary">
                No images here yet.
              </Typography>
            )}
          </LibraryLayout.Section>
        </>
      )}
    </LibraryLayout.Container>
  );
}

export function LibraryImages({ DraggableImage }: { DraggableImage: React.ComponentType<{ url: string; title: string }> }) {
  usePrefetchQuery(imagesQueries.list);

  return (
    <Suspense
      fallback={
        <LibraryLayout.Container>
          <LibraryLayout.Section title="Folders">
            <LibraryLayout.FolderListItem isLoading />
            <LibraryLayout.FolderListItem isLoading />
            <LibraryLayout.FolderListItem isLoading />
          </LibraryLayout.Section>

          <LibraryLayout.Section title="Images">
            <ImagesLayout.Gallery>
              <ImagesLayout.GalleryTile key={Math.random()}>
                <ImagesLayout.GalleryTileImage>
                  <Skeleton variant="rectangular" width="100%" height="100%" />
                </ImagesLayout.GalleryTileImage>
              </ImagesLayout.GalleryTile>

              <ImagesLayout.GalleryTile key={Math.random()}>
                <ImagesLayout.GalleryTileImage>
                  <Skeleton variant="rectangular" width="100%" height="100%" />
                </ImagesLayout.GalleryTileImage>
              </ImagesLayout.GalleryTile>

              <ImagesLayout.GalleryTile key={Math.random()}>
                <ImagesLayout.GalleryTileImage>
                  <Skeleton variant="rectangular" width="100%" height="100%" />
                </ImagesLayout.GalleryTileImage>
              </ImagesLayout.GalleryTile>

              <ImagesLayout.GalleryTile key={Math.random()}>
                <ImagesLayout.GalleryTileImage>
                  <Skeleton variant="rectangular" width="100%" height="100%" />
                </ImagesLayout.GalleryTileImage>
              </ImagesLayout.GalleryTile>
            </ImagesLayout.Gallery>
          </LibraryLayout.Section>
        </LibraryLayout.Container>
      }
    >
      <ErrorBoundary fallback={<ImagesListErrorState title="Error" description="Something went wrong" />}>
        <LibraryImagesContent DraggableImage={DraggableImage} />
      </ErrorBoundary>
    </Suspense>
  );
}
